import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ApiError } from "../api/client.js";
import type {
  DesignDecisionsPatch,
  DesignDecisionsResponse,
  DesignSystemType,
} from "../api/types.js";
import { usePortal } from "../app/context.js";
import { href } from "../app/router.js";
import { humanise } from "../lib/format.js";

/**
 * Manual heat-pump selection for one job.
 *
 * Josh's decision (handoff §07): product selection is MANUAL. The installer
 * types the make, model and capacity of the heat pump they've chosen — no
 * catalogue picker — and the platform's own calc engine (checkSizing) tells
 * them whether it passes MIS 3005-D §3.4.1(e) at the design condition.
 *
 * This screen is a thin form over the existing PUT/GET
 * /api/jobs/:jobId/design-decisions endpoints. The API already runs
 * computeDesignPackFigures on every GET and returns the calc warnings inline,
 * so a designer types a spec, saves, and sees the sizing verdict without a
 * separate roundtrip. No new backend route was needed for this screen.
 */

const SYSTEM_TYPES: readonly DesignSystemType[] = [
  "ashp",
  "gshp",
  "hybrid",
  "air-to-air",
];

interface FormState {
  systemType: DesignSystemType;
  heatPumpManufacturer: string;
  heatPumpModel: string;
  heatPumpCapacityAtDesignW: string;
  heatPumpDhwOutputW: string;
  heatPumpScop: string;
  heatPumpScopSource: string;
}

const EMPTY: FormState = {
  systemType: "ashp",
  heatPumpManufacturer: "",
  heatPumpModel: "",
  heatPumpCapacityAtDesignW: "",
  heatPumpDhwOutputW: "",
  heatPumpScop: "",
  heatPumpScopSource: "",
};

function toPatch(state: FormState): DesignDecisionsPatch {
  const num = (v: string) => (v.trim() === "" ? undefined : Number(v));
  return {
    systemType: state.systemType,
    ...(state.heatPumpManufacturer.trim()
      ? { heatPumpManufacturer: state.heatPumpManufacturer.trim() }
      : {}),
    ...(state.heatPumpModel.trim()
      ? { heatPumpModel: state.heatPumpModel.trim() }
      : {}),
    ...(num(state.heatPumpCapacityAtDesignW) !== undefined
      ? { heatPumpCapacityAtDesignW: num(state.heatPumpCapacityAtDesignW) }
      : {}),
    ...(num(state.heatPumpDhwOutputW) !== undefined
      ? { heatPumpDhwOutputW: num(state.heatPumpDhwOutputW) }
      : {}),
    ...(num(state.heatPumpScop) !== undefined
      ? { heatPumpScop: num(state.heatPumpScop) }
      : {}),
    ...(state.heatPumpScopSource.trim()
      ? { heatPumpScopSource: state.heatPumpScopSource.trim() }
      : {}),
  };
}

function stateFrom(current: DesignDecisionsResponse | null): FormState {
  const d = current?.decisions;
  if (!d) return EMPTY;
  return {
    systemType: d.systemType ?? "ashp",
    heatPumpManufacturer: d.heatPumpManufacturer ?? "",
    heatPumpModel: d.heatPumpModel ?? "",
    heatPumpCapacityAtDesignW:
      d.heatPumpCapacityAtDesignW !== undefined
        ? String(d.heatPumpCapacityAtDesignW)
        : "",
    heatPumpDhwOutputW:
      d.heatPumpDhwOutputW !== undefined ? String(d.heatPumpDhwOutputW) : "",
    heatPumpScop: d.heatPumpScop !== undefined ? String(d.heatPumpScop) : "",
    heatPumpScopSource: d.heatPumpScopSource ?? "",
  };
}

export function HeatPumpSelection({ jobId }: { jobId: string }): ReactNode {
  const { client } = usePortal();
  const [state, setState] = useState<FormState>(EMPTY);
  const [current, setCurrent] = useState<DesignDecisionsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await client.designDecisions(jobId);
      setCurrent(res);
      setState(stateFrom(res));
      setError(null);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "The design decisions could not be loaded. Try reloading the screen.",
      );
    } finally {
      setLoading(false);
    }
  }, [client, jobId]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = useCallback(<K extends keyof FormState>(k: K, v: FormState[K]) => {
    setState((prev) => ({ ...prev, [k]: v }));
  }, []);

  const onSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      setSaving(true);
      setError(null);
      try {
        await client.updateDesignDecisions(jobId, toPatch(state));
        await load();
      } catch (caught) {
        setError(
          caught instanceof ApiError
            ? caught.message
            : "The heat-pump decision could not be saved. Try again.",
        );
      } finally {
        setSaving(false);
      }
    },
    [client, jobId, load, state],
  );

  return (
    <>
      <div className="page-header">
        <h1>Heat pump — manual selection</h1>
        <p className="lede">
          Enter the heat pump you've chosen. The platform's own calc engine
          checks it against MIS 3005-D §3.4.1(e) at the job's design condition
          and shows the verdict below — pass, undersized, or "alternative
          design at ≤55°C required".
        </p>
        <p>
          <a href={href.job(jobId)}>Back to the job</a>
        </p>
      </div>

      {loading ? (
        <p aria-live="polite">Loading the design decisions…</p>
      ) : null}

      <form onSubmit={onSubmit} className="stack" aria-labelledby="hp-form-heading">
        <h2 id="hp-form-heading">Product</h2>

        <fieldset className="stack">
          <legend>Heat pump</legend>

          <label className="field">
            <span>System type</span>
            <select
              value={state.systemType}
              onChange={(e) => patch("systemType", e.target.value as DesignSystemType)}
            >
              {SYSTEM_TYPES.map((t) => (
                <option key={t} value={t}>
                  {humanise(t)}
                </option>
              ))}
            </select>
          </label>

          <label className="field">
            <span>Manufacturer</span>
            <input
              type="text"
              value={state.heatPumpManufacturer}
              onChange={(e) => patch("heatPumpManufacturer", e.target.value)}
              placeholder="e.g. Vaillant"
            />
          </label>

          <label className="field">
            <span>Model</span>
            <input
              type="text"
              value={state.heatPumpModel}
              onChange={(e) => patch("heatPumpModel", e.target.value)}
              placeholder="e.g. aroTHERM plus VWL 105/6 A"
            />
          </label>

          <label className="field">
            <span>Capacity at design condition (W)</span>
            <input
              type="number"
              step="1"
              value={state.heatPumpCapacityAtDesignW}
              onChange={(e) =>
                patch("heatPumpCapacityAtDesignW", e.target.value)
              }
              placeholder="e.g. 8000"
            />
            <span className="muted small">
              At the design flow temp and design external temp, excluding any
              supplementary electric heater. From the MCS Product Directory.
            </span>
          </label>

          <label className="field">
            <span>DHW output (W)</span>
            <input
              type="number"
              step="1"
              value={state.heatPumpDhwOutputW}
              onChange={(e) => patch("heatPumpDhwOutputW", e.target.value)}
              placeholder="e.g. 7000"
            />
          </label>

          <label className="field">
            <span>SCOP</span>
            <input
              type="number"
              step="0.01"
              value={state.heatPumpScop}
              onChange={(e) => patch("heatPumpScop", e.target.value)}
              placeholder="e.g. 3.2"
            />
          </label>

          <label className="field">
            <span>SCOP source</span>
            <input
              type="text"
              value={state.heatPumpScopSource}
              onChange={(e) => patch("heatPumpScopSource", e.target.value)}
              placeholder="e.g. MCS 026 calculation"
            />
          </label>
        </fieldset>

        {error ? (
          <p className="error" role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" className="primary" disabled={saving}>
          {saving ? "Saving…" : "Save decision"}
        </button>
      </form>

      {current?.computed ? (
        <VerdictSection response={current} />
      ) : current !== null ? (
        <NoVerdictYet missing={current.missing} />
      ) : null}
    </>
  );
}

function VerdictSection({
  response,
}: {
  response: DesignDecisionsResponse;
}): ReactNode {
  const warnings = response.computed?.warnings ?? [];
  const sizingWarnings = warnings.filter(
    (w) => w.code.startsWith("MIS3005D_") || w.code === "INVALID_HEAT_LOAD",
  );
  const complianceFailures = sizingWarnings.filter(
    (w) => w.severity === "compliance-failure",
  );

  return (
    <section aria-labelledby="hp-verdict" className="stack">
      <h2 id="hp-verdict">Sizing verdict</h2>

      <p
        className={
          complianceFailures.length === 0 ? "verdict eligible" : "verdict ineligible"
        }
        role="status"
      >
        <strong>
          {complianceFailures.length === 0
            ? "Passes MIS 3005-D §3.4.1(e)"
            : "Does not pass"}
        </strong>{" "}
        — from the calc engine, against the job's design heat load and design
        flow temperature.
      </p>

      {sizingWarnings.length > 0 ? (
        <ul className="warnings">
          {sizingWarnings.map((w, i) => (
            <li key={`${w.code}-${i}`}>
              <strong>{w.message}</strong>
              <div className="muted small">
                <code>{w.code}</code> · severity {w.severity}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {response.missing.length > 0 ? (
        <>
          <h3>Design pack still needs</h3>
          <ul className="missing">
            {response.missing.map((m, i) => (
              <li key={i}>{m}</li>
            ))}
          </ul>
        </>
      ) : null}
    </section>
  );
}

function NoVerdictYet({ missing }: { missing: readonly string[] }): ReactNode {
  return (
    <section aria-labelledby="hp-verdict" className="stack">
      <h2 id="hp-verdict">Sizing verdict</h2>
      <p className="muted">
        No verdict yet — the calc chain needs a survey on this job before it
        can compute the design heat load a chosen heat pump is measured
        against.
      </p>
      {missing.length > 0 ? (
        <ul className="missing">
          {missing.map((m, i) => (
            <li key={i}>{m}</li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
